import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Eye, EyeOff, CheckCircle, XCircle, FileText, Database, Copy, Check } from 'lucide-react';
import { APIs } from '../config/apis';
import { getIcon } from '../config/icons';
import { JsonFieldRenderer } from './JsonFieldRenderer';
import { toast } from 'react-toastify';

const ResultsPanel = ({ 
  searchResults, 
  expandedResults, 
  toggleExpandedResult
}) => {
  console.log('🎯 ResultsPanel: recebeu searchResults:', Object.keys(searchResults || {}));
  
  // Estado para controlar feedback visual do botão de copiar
  const [copiedStates, setCopiedStates] = useState({});
  
  if (!searchResults || Object.keys(searchResults).length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full mb-6">
            <Database className="w-10 h-10 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Nenhuma consulta realizada
          </h3>
          <p className="text-gray-600">
            Faça uma busca para ver os resultados completos das APIs aqui
          </p>
        </div>
      </div>
    );
  }

  // Função para copiar dados para clipboard
  const copyToClipboard = (text, apiKey) => {
    console.log('🔍 Tentando copiar dados:', text);
    console.log('🔍 Tipo do texto:', typeof text);
    console.log('🔍 Tamanho do texto:', text ? text.length : 0);
    
    if (!text) {
      console.error('❌ Nenhum texto para copiar');
      toast.error('❌ Nenhum dado para copiar');
      return;
    }
    
    // Verificar se o texto é válido
    if (typeof text !== 'string') {
      console.error('❌ Texto não é uma string:', typeof text);
      toast.error('❌ Dados inválidos para copiar');
      return;
    }
    
    // Mostrar feedback visual imediato
    setCopiedStates(prev => ({ ...prev, [apiKey]: true }));
    
    // Verificar se o navegador suporta clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        console.log('✅ Dados copiados para o clipboard com sucesso!');
        toast.success('✅ JSON copiado para a área de transferência!');
        
        // Resetar feedback visual após 2 segundos
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [apiKey]: false }));
        }, 2000);
      }).catch(err => {
        console.error('❌ Erro ao copiar dados:', err);
        console.error('❌ Detalhes do erro:', err.message);
        
        // Tentar fallback
        tryFallbackCopy(text, apiKey);
      });
    } else {
      console.log('⚠️ Clipboard API não suportada, usando fallback');
      tryFallbackCopy(text, apiKey);
    }
  };
  
  // Função de fallback para cópia
  const tryFallbackCopy = (text, apiKey) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        console.log('✅ Dados copiados usando fallback!');
        toast.success('✅ JSON copiado para a área de transferência!');
        
        // Resetar feedback visual após 2 segundos
        setTimeout(() => {
          setCopiedStates(prev => ({ ...prev, [apiKey]: false }));
        }, 2000);
      } else {
        throw new Error('execCommand retornou false');
      }
    } catch (fallbackErr) {
      console.error('❌ Erro no fallback também:', fallbackErr);
      toast.error('❌ Erro ao copiar JSON. Tente selecionar e copiar manualmente.');
      setCopiedStates(prev => ({ ...prev, [apiKey]: false }));
    }
  };

  // Função para ordenar as APIs - Banco Bitrix sempre primeiro
  const sortApisByPriority = (searchResults) => {
    // Verificar se searchResults é válido
    if (!searchResults || typeof searchResults !== 'object') {
      return {};
    }
    
    const sortedEntries = Object.entries(searchResults).sort(([apiKeyA], [apiKeyB]) => {
      // API Banco Bitrix sempre primeiro
      if (apiKeyA === 'apiBancoBitrix') return -1;
      if (apiKeyB === 'apiBancoBitrix') return 1;
      
      // Demais APIs mantêm ordem original
      return 0;
    });
    
    return Object.fromEntries(sortedEntries);
  };

  // Função para renderizar dados de forma amigável
  const renderDataPreview = (data) => {
    if (!data) return null;
    
    if (Array.isArray(data)) {
      return (
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">
            📊 {data.length} resultado(s) encontrado(s)
          </div>
          {data.slice(0, 3).map((item, index) => {
            // Verificar se item é válido
            if (!item || typeof item !== 'object') {
              return (
                <div key={index} className="bg-gray-50 p-2 rounded-md text-xs">
                  <div className="font-medium text-gray-800">
                    Item {index + 1} (dados inválidos)
                  </div>
                </div>
              );
            }
            
            return (
              <div key={index} className="bg-gray-50 p-2 rounded-md text-xs">
                <div className="font-medium text-gray-800">
                  {item.id || item.cobrancaID || `Item ${index + 1}`}
                </div>
                {item.nomePagador && (
                  <div className="text-gray-600">👤 {item.nomePagador}</div>
                )}
                {item.valor && (
                  <div className="text-gray-600">💰 R$ {item.valor}</div>
                )}
                {item.statusCobrancaTexto && (
                  <div className="text-gray-600">📋 {item.statusCobrancaTexto}</div>
                )}
                {item.vencimentoString && (
                  <div className="text-gray-600">📅 {item.vencimentoString}</div>
                )}
              </div>
            );
          })}
          {data.length > 3 && (
            <div className="text-xs text-gray-500">
              ... e mais {data.length - 3} resultado(s)
            </div>
          )}
        </div>
      );
    }
    
    // Para objetos, mostrar campos principais
    const mainFields = ['cobrancaID', 'nomePagador', 'valor', 'statusCobrancaTexto', 'vencimentoString'];
    const availableFields = mainFields.filter(field => data[field]);
    
    return (
      <div className="space-y-2">
        {availableFields.map(field => (
          <div key={field} className="bg-gray-50 p-2 rounded-md text-xs">
            <div className="font-medium text-gray-800 capitalize">
              {field.replace(/([A-Z])/g, ' $1').trim()}:
            </div>
            <div className="text-gray-600">
              {field === 'valor' ? `R$ ${data[field]}` : data[field]}
            </div>
          </div>
        ))}
        
        {/* Mostrar resumo de vendas se existir */}
        {data.resumoDeVendas && (
          <div className="bg-blue-50 p-2 rounded-md text-xs">
            <div className="font-medium text-blue-800">📋 Resumo de Vendas:</div>
            <div className="text-blue-700 line-clamp-2">
              {data.resumoDeVendas.substring(0, 100)}
              {data.resumoDeVendas.length > 100 && '...'}
            </div>
          </div>
        )}
      </div>
    );
  };

  const sortedResults = sortApisByPriority(searchResults);

  return (
    <div className="h-full flex flex-col">
      {/* Header do painel */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-3">
                  <div className="flex items-center justify-between mb-1">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Resultados Detalhados</h2>
              <p className="text-xs text-gray-600">
                {Object.keys(sortedResults).length} API(s) consultada(s)
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Resultados das APIs */}
      <div className="flex-1 overflow-y-auto space-y-2">
        <AnimatePresence>
          {Object.entries(sortedResults).map(([apiKey, result]) => {
          // Verificar se result é válido
          if (!result || typeof result !== 'object') {
            return (
              <motion.div
                key={apiKey}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-gray-500">
                        <Database className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{apiKey}</h3>
                        <p className="text-sm text-gray-600">API não disponível</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 text-red-600">
                      <XCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Erro</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          }
          
          const api = APIs[apiKey] || { 
            name: result.name || 'API Desconhecida', 
            icon: 'Search',
            color: result.color || 'blue'
          };
          const IconComponent = getIcon(api.icon);
          const isExpanded = expandedResults[apiKey];
          
          
          return (
            <motion.div
              key={apiKey}
              data-api-key={apiKey}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Header do resultado */}
              <div className="p-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${api.color}`}>
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{api.name}</h3>
                      <p className="text-sm text-gray-600">{api.description}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {/* Status do resultado */}
                    <div className="flex items-center space-x-2">
                      {result.status === 'searching' || result.found === null ? (
                        <div className="flex items-center space-x-1 text-blue-600">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="text-sm font-medium">Buscando...</span>
                        </div>
                      ) : result.found === true ? (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Encontrado</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1 text-red-600">
                          <XCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Não encontrado</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Botão expandir/colapsar */}
                    <button
                      onClick={() => toggleExpandedResult(apiKey)}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={isExpanded ? 'Colapsar' : 'Expandir'}
                    >
                      {isExpanded ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Informações da busca */}
                {result.searchMethod && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <div className="text-sm text-blue-800">
                      <span className="font-medium">Método:</span> {result.searchMethod}
                      {result.searchSteps && Array.isArray(result.searchSteps) && result.searchSteps.length > 0 && (
                        <span className="ml-2">
                          <span className="font-medium">Passos:</span> {result.searchSteps.join(' → ')}
                        </span>
                      )}
                    </div>
                    {result.foundParcela && (
                      <div className="text-sm text-blue-700 mt-1">
                        <span className="font-medium">Arquivo:</span> {result.foundParcela}
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Conteúdo expandido */}
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="p-3"
                >
                  {result.status === 'searching' || result.found === null ? (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">
                        Buscando dados...
                      </h4>
                      <p className="text-gray-600">
                        Aguarde enquanto consultamos a API
                      </p>
                    </div>
                  ) : result.found === true && result.data ? (
                    <div>
                      {/* Dados completos */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-gray-900 text-xs">
                            📊 Dados Completos
                            {Array.isArray(result.data) && result.data.length > 1 && (
                              <span className="ml-2 text-blue-600">({result.data.length} cobranças)</span>
                            )}
                          </h4>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                console.log('🔍 Dados do resultado:', result.data);
                                const jsonString = JSON.stringify(result.data, null, 2);
                                console.log('🔍 JSON string gerado:', jsonString);
                                console.log('🔍 Tamanho do JSON:', jsonString.length);
                                copyToClipboard(jsonString, apiKey);
                              }}
                              className={`flex items-center space-x-1 text-xs px-2 py-1 rounded transition-all duration-200 ${
                                copiedStates[apiKey] 
                                  ? 'text-green-600 bg-green-50 border border-green-200' 
                                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                              }`}
                            >
                              {copiedStates[apiKey] ? (
                                <>
                                  <Check className="w-4 h-4" />
                                  <span>Copiado!</span>
                                </>
                              ) : (
                                <>
                                  <Copy className="w-4 h-4" />
                                  <span>Copiar JSON</span>
                                </>
                              )}
                            </button>
                            
                            {/* Botão alternativo para mostrar JSON em modal */}
                            <button
                              onClick={() => {
                                const jsonString = JSON.stringify(result.data, null, 2);
                                // Criar um modal simples para mostrar o JSON
                                const modal = document.createElement('div');
                                modal.style.cssText = `
                                  position: fixed;
                                  top: 0;
                                  left: 0;
                                  width: 100%;
                                  height: 100%;
                                  background: rgba(0,0,0,0.8);
                                  z-index: 9999;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  padding: 20px;
                                `;
                                
                                const content = document.createElement('div');
                                content.style.cssText = `
                                  background: white;
                                  border-radius: 8px;
                                  padding: 20px;
                                  max-width: 80%;
                                  max-height: 80%;
                                  overflow: auto;
                                  position: relative;
                                `;
                                
                                const closeBtn = document.createElement('button');
                                closeBtn.innerHTML = '✕';
                                closeBtn.style.cssText = `
                                  position: absolute;
                                  top: 10px;
                                  right: 10px;
                                  background: #ef4444;
                                  color: white;
                                  border: none;
                                  border-radius: 50%;
                                  width: 30px;
                                  height: 30px;
                                  cursor: pointer;
                                  font-size: 16px;
                                `;
                                closeBtn.onclick = () => document.body.removeChild(modal);
                                
                                const textarea = document.createElement('textarea');
                                textarea.value = jsonString;
                                textarea.style.cssText = `
                                  width: 100%;
                                  height: 400px;
                                  font-family: monospace;
                                  font-size: 12px;
                                  border: 1px solid #ccc;
                                  border-radius: 4px;
                                  padding: 10px;
                                  resize: vertical;
                                `;
                                textarea.readOnly = true;
                                
                                const copyBtn = document.createElement('button');
                                copyBtn.innerHTML = '📋 Copiar Tudo';
                                copyBtn.style.cssText = `
                                  margin-top: 10px;
                                  padding: 8px 16px;
                                  background: #3b82f6;
                                  color: white;
                                  border: none;
                                  border-radius: 4px;
                                  cursor: pointer;
                                `;
                                copyBtn.onclick = () => {
                                  textarea.select();
                                  document.execCommand('copy');
                                  copyBtn.innerHTML = '✅ Copiado!';
                                  setTimeout(() => {
                                    copyBtn.innerHTML = '📋 Copiar Tudo';
                                  }, 2000);
                                };
                                
                                content.appendChild(closeBtn);
                                content.appendChild(document.createElement('h3')).textContent = 'JSON Completo';
                                content.appendChild(textarea);
                                content.appendChild(copyBtn);
                                modal.appendChild(content);
                                document.body.appendChild(modal);
                              }}
                              className="flex items-center space-x-1 text-xs px-2 py-1 rounded transition-colors text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                            >
                              <FileText className="w-4 h-4" />
                              <span>Ver JSON</span>
                            </button>
                          </div>
                        </div>
                        {/* Verificar se result.data é array ou objeto único */}
                        {(() => {
                          console.log('🔍 ResultsPanel: Renderizando dados', {
                            isArray: Array.isArray(result.data),
                            length: Array.isArray(result.data) ? result.data.length : 'N/A',
                            data: result.data
                          });
                          
                          if (!result.data) {
                            return (
                              <div className="text-center py-4 text-gray-500">
                                ⚠️ Nenhum dado para exibir
                              </div>
                            );
                          }
                          
                          if (Array.isArray(result.data)) {
                            if (result.data.length === 0) {
                              return (
                                <div className="text-center py-4 text-gray-500">
                                  ⚠️ Array vazio
                                </div>
                              );
                            }
                            
                            // Renderizar cada cobrança em um CARD SEPARADO
                            return (
                              <div className="space-y-4">
                                {result.data.map((cobranca, index) => {
                                  console.log(`🔍 Renderizando cobrança ${index + 1}:`, cobranca);
                                  
                                  return (
                                    <div key={index} className="bg-white border-2 border-purple-300 rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                                      {/* Cabeçalho do card individual - COMPACTO E ROXO */}
                                      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 px-3 py-2">
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-white text-purple-700 shadow-sm">
                                                {index + 1} de {result.data.length}
                                              </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                              <div className="flex-1">
                                                <h5 className="font-bold text-white text-base flex items-center">
                                                  {cobranca.codigoFalanteRadar && (
                                                    <span className="flex items-center">
                                                      🔖 {cobranca.codigoFalanteRadar}
                                                    </span>
                                                  )}
                                                </h5>
                                                <div className="text-purple-100 text-xs mt-0.5 flex items-center space-x-3">
                                                  {cobranca.nomePagador && (
                                                    <span className="flex items-center">
                                                      <span className="mr-1">👤</span>
                                                      {cobranca.nomePagador}
                                                    </span>
                                                  )}
                                                  {cobranca.valor && (
                                                    <span className="flex items-center font-bold text-yellow-300">
                                                      <span className="mr-1">💰</span>
                                                      R$ {parseFloat(cobranca.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Botões de ação no cabeçalho - COMPACTOS */}
                                          <div className="flex items-center space-x-1.5 ml-3">
                                            <button
                                              onClick={() => {
                                                const jsonString = JSON.stringify(cobranca, null, 2);
                                                copyToClipboard(jsonString, `${apiKey}-${index}`);
                                              }}
                                              className={`flex items-center space-x-1 text-[10px] px-2 py-1 rounded-md transition-all duration-200 font-semibold ${
                                                copiedStates[`${apiKey}-${index}`] 
                                                  ? 'bg-green-500 text-white' 
                                                  : 'bg-white text-purple-700 hover:bg-purple-50'
                                              }`}
                                              title="Copiar JSON"
                                            >
                                              {copiedStates[`${apiKey}-${index}`] ? (
                                                <>
                                                  <Check className="w-3 h-3" />
                                                  <span>Copiado!</span>
                                                </>
                                              ) : (
                                                <>
                                                  <Copy className="w-3 h-3" />
                                                  <span>Copiar JSON</span>
                                                </>
                                              )}
                                            </button>
                                            
                                            <button
                                              onClick={() => {
                                                const jsonString = JSON.stringify(cobranca, null, 2);
                                                // Criar modal
                                                const modal = document.createElement('div');
                                                modal.style.cssText = `
                                                  position: fixed;
                                                  top: 0;
                                                  left: 0;
                                                  width: 100%;
                                                  height: 100%;
                                                  background: rgba(0,0,0,0.85);
                                                  z-index: 9999;
                                                  display: flex;
                                                  align-items: center;
                                                  justify-content: center;
                                                  padding: 20px;
                                                  backdrop-filter: blur(4px);
                                                `;
                                                
                                                const content = document.createElement('div');
                                                content.style.cssText = `
                                                  background: white;
                                                  border-radius: 16px;
                                                  padding: 24px;
                                                  max-width: 90%;
                                                  max-height: 90%;
                                                  overflow: auto;
                                                  position: relative;
                                                  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
                                                `;
                                                
                                                const header = document.createElement('div');
                                                header.style.cssText = `
                                                  display: flex;
                                                  justify-content: space-between;
                                                  align-items: center;
                                                  margin-bottom: 16px;
                                                  padding-bottom: 16px;
                                                  border-bottom: 2px solid #e5e7eb;
                                                `;
                                                
                                                const title = document.createElement('h3');
                                                title.textContent = `JSON Completo - ${cobranca.codigoFalanteRadar || 'Cobrança'}`;
                                                title.style.cssText = `
                                                  font-size: 20px;
                                                  font-weight: bold;
                                                  color: #1f2937;
                                                  margin: 0;
                                                `;
                                                
                                                const closeBtn = document.createElement('button');
                                                closeBtn.innerHTML = '✕';
                                                closeBtn.style.cssText = `
                                                  background: #ef4444;
                                                  color: white;
                                                  border: none;
                                                  border-radius: 50%;
                                                  width: 36px;
                                                  height: 36px;
                                                  cursor: pointer;
                                                  font-size: 18px;
                                                  font-weight: bold;
                                                  transition: all 0.2s;
                                                  display: flex;
                                                  align-items: center;
                                                  justify-content: center;
                                                `;
                                                closeBtn.onmouseover = () => {
                                                  closeBtn.style.background = '#dc2626';
                                                  closeBtn.style.transform = 'scale(1.1)';
                                                };
                                                closeBtn.onmouseout = () => {
                                                  closeBtn.style.background = '#ef4444';
                                                  closeBtn.style.transform = 'scale(1)';
                                                };
                                                closeBtn.onclick = () => document.body.removeChild(modal);
                                                
                                                const textarea = document.createElement('textarea');
                                                textarea.value = jsonString;
                                                textarea.style.cssText = `
                                                  width: 100%;
                                                  height: 500px;
                                                  font-family: 'Courier New', monospace;
                                                  font-size: 13px;
                                                  border: 2px solid #d1d5db;
                                                  border-radius: 8px;
                                                  padding: 16px;
                                                  resize: vertical;
                                                  background: #f9fafb;
                                                  color: #111827;
                                                `;
                                                textarea.readOnly = true;
                                                
                                                const copyBtn = document.createElement('button');
                                                copyBtn.innerHTML = '📋 Copiar Tudo';
                                                copyBtn.style.cssText = `
                                                  margin-top: 12px;
                                                  padding: 10px 20px;
                                                  background: #3b82f6;
                                                  color: white;
                                                  border: none;
                                                  border-radius: 8px;
                                                  cursor: pointer;
                                                  font-size: 14px;
                                                  font-weight: 600;
                                                  transition: all 0.2s;
                                                  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                                                `;
                                                copyBtn.onmouseover = () => {
                                                  copyBtn.style.background = '#2563eb';
                                                  copyBtn.style.transform = 'translateY(-2px)';
                                                  copyBtn.style.boxShadow = '0 6px 8px -1px rgba(0, 0, 0, 0.15)';
                                                };
                                                copyBtn.onmouseout = () => {
                                                  copyBtn.style.background = '#3b82f6';
                                                  copyBtn.style.transform = 'translateY(0)';
                                                  copyBtn.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                                                };
                                                copyBtn.onclick = () => {
                                                  textarea.select();
                                                  document.execCommand('copy');
                                                  copyBtn.innerHTML = '✅ Copiado!';
                                                  copyBtn.style.background = '#10b981';
                                                  setTimeout(() => {
                                                    copyBtn.innerHTML = '📋 Copiar Tudo';
                                                    copyBtn.style.background = '#3b82f6';
                                                  }, 2000);
                                                };
                                                
                                                header.appendChild(title);
                                                header.appendChild(closeBtn);
                                                content.appendChild(header);
                                                content.appendChild(textarea);
                                                content.appendChild(copyBtn);
                                                modal.appendChild(content);
                                                document.body.appendChild(modal);
                                                
                                                // Fechar com ESC
                                                const handleEsc = (e) => {
                                                  if (e.key === 'Escape') {
                                                    document.body.removeChild(modal);
                                                    document.removeEventListener('keydown', handleEsc);
                                                  }
                                                };
                                                document.addEventListener('keydown', handleEsc);
                                              }}
                                              className="flex items-center space-x-1 text-[10px] px-2 py-1 rounded-md transition-all duration-200 font-semibold bg-white text-indigo-700 hover:bg-indigo-50"
                                              title="Ver JSON Completo"
                                            >
                                              <FileText className="w-3 h-3" />
                                              <span>Ver JSON</span>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                      
                                      {/* Conteúdo do card */}
                                      <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-3 max-h-[500px] overflow-y-auto">
                                        <JsonFieldRenderer data={cobranca} />
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          } else {
                            // Renderizar objeto único em um card
                            console.log('🔍 Renderizando objeto único:', result.data);
                            return (
                              <div className="bg-gray-50 rounded-lg p-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 150px)' }}>
                                <JsonFieldRenderer data={result.data} />
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                      <h4 className="text-lg font-medium text-gray-900 mb-2">
                        Nenhum dado encontrado
                      </h4>
                      <p className="text-gray-600">
                        {result.error || 'A API não retornou dados para esta consulta'}
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          );
        })}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ResultsPanel;
